/**
 * ProfessocialTopic stellt alle Module zur Verfügung, die für die Topics nötig sind
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TopicsOverview from '../shared/components/ModuleComponents/TopicsOverview'
import TopicEdit from '../shared/components/ModuleComponents/TopicEdit'
import {
  acknowledgeSettingsForNetworkPageInitiated,
  createAboTopicInitiated,
  deleteAboTopicInitiated,
  getAboSettingsForCurrentUserInitiated,
  getAboTopicHasPostingsInitiated,
  getTopicsAndMetadataForCurrentUserInitiated,
  updateAboSettingsForCurrentUserInitiated,
  updateAboTopicInitiated,
} from '../store/topic/topic.actions'

import * as commonActions from '../store/common/common.actions'
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router'
import {
  CreateAboTopicRequest,
  DeleteAboTopicRequest,
  GetAboTopicHasPostingsRequest,
  UpdateAboTopicRequest,
} from '@3m5/sm_publish-frontend-api'
import {
  AboType,
  NetworkType,
  Topic,
  TopicChangeCascadeLevel,
  TopicsWithMetadataResponse,
  UserAboSettings,
  UserAboSettingsUpdateElement,
} from '@3m5/sm_publish-frontend-api'
import { TopicWithMetadata } from '@3m5/sm_publish-frontend-api'
import { getAboHasPostings, getAboSettings, getAboTopics, getReNewSettingsStatus } from '../store/topic/topic.selector'
import { DisplayMode, TopicConfiguration } from '@professocial/professocialTypes/types'
import {
  AcknowledgeSettingsForNetworkPageRequest,
  UpdateAboSettingsForCurrentUserRequest,
} from '@3m5/sm_publish-frontend-api'
import { useTranslation } from 'react-i18next'
import { namedRequestData } from '../store/common/common.selectors'
import { TopicRootState } from '../store/topicRoot.reducer'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { AboHasPostingsObject } from '../store/topic/topic.reducer'
import { SubmenuData } from '@professocial/professocialFormComponents'
import Submenu from '@professocial/professocialFormComponents/FormComponents/Submenu'

export interface Props {
  topicConfiguration?: TopicConfiguration
}

export interface NetworkAboSettings {
  [key: string]: UserAboSettings
}

const ProfessocialTopics: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { path } = useRouteMatch()
  const history = useHistory()

  const initialValueDropdown = props.topicConfiguration?.initialValueDropdown || false
  const editable = props.topicConfiguration?.editable || false
  const editMode = props.topicConfiguration?.editMode || DisplayMode.newScreen
  const integrateImages = props.topicConfiguration?.integrateImages || false

  const [networkAboSettings, setNetworkAboSettings] = useState<NetworkAboSettings>({})
  const [showTopicPopUp, setShowTopicPopUp] = useState<boolean>(false)
  const [toast, setToast] = React.useState<any>()
  const [tabs, setTabs] = React.useState<NetworkType[]>([])
  const [showChangeDeferredRequest, setShowChangeDeferredRequest] = useState<boolean>(false)
  const [changedSettingsTopicIds, setChangedSettingsTopicIds] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<NetworkType>(NetworkType.Facebook)

  const dispatch = useDispatch()
  const dispatchGetAboTopics = useCallback(() => dispatch(getTopicsAndMetadataForCurrentUserInitiated()), [dispatch])
  const dispatchGetAboSettings = useCallback(() => dispatch(getAboSettingsForCurrentUserInitiated()), [dispatch])
  const dispatchCreateAboTopic = useCallback((aboTopic: CreateAboTopicRequest) => dispatch(createAboTopicInitiated(aboTopic)), [dispatch])
  const dispatchDeleteAboTopic = useCallback((aboTopic: DeleteAboTopicRequest) => dispatch(deleteAboTopicInitiated(aboTopic)), [dispatch])
  const dispatchUpdateAboTopic = useCallback((aboTopic: UpdateAboTopicRequest) => dispatch(updateAboTopicInitiated(aboTopic)), [dispatch])
  const dispatchUpdateAboSettings = useCallback((params: UpdateAboSettingsForCurrentUserRequest) => dispatch(updateAboSettingsForCurrentUserInitiated(params)), [dispatch])
  const dispatchAcknowledgeSettingsForNetworkPage = useCallback((params: AcknowledgeSettingsForNetworkPageRequest) => dispatch(acknowledgeSettingsForNetworkPageInitiated(params)), [dispatch])
  // const dispatchGetAuditLogForCurrentUser = useCallback((params: GetAuditLogForCurrentUserRequest) => dispatch(getAuditLogForCurrentUserInitiated(params)), [dispatch])
  const dispatchGetAboTopicHasPostings = useCallback((params: GetAboTopicHasPostingsRequest) => dispatch(getAboTopicHasPostingsInitiated(params)), [dispatch])

  const aboTopicHasPostings: AboHasPostingsObject = useSelector(getAboHasPostings)
  const aboTopics: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)
  const aboSettings: UserAboSettings[] | undefined = useSelector(getAboSettings)
  const reNewSettingsStatus: boolean = useSelector(getReNewSettingsStatus)
  const actionOnTopicCreate: ActionCall | undefined = useSelector((state: TopicRootState) => namedRequestData(state, createAboTopicInitiated.type))
  const actionOnTopicUpdate: ActionCall | undefined = useSelector((state: TopicRootState) => namedRequestData(state, updateAboTopicInitiated.type))
  const actionOnTopicDelete: ActionCall | undefined = useSelector((state: TopicRootState) => namedRequestData(state, deleteAboTopicInitiated.type))

  const getNetworkPage = (networkType: NetworkType) => {
    switch (networkType) {
    case NetworkType.Facebook:
      return props.topicConfiguration?.networkPage?.facebookPage
    case NetworkType.Instagram:
      return props.topicConfiguration?.networkPage?.instagramPage
    case NetworkType.GoogleMyBusiness:
      return props.topicConfiguration?.networkPage?.googleMyBusinessPage
    }
  }

  const subMenu: SubmenuData[] = [
    {
      id: 'channels',
      label: t('topicsCommon:topics'),
    },
    {
      id: 'categories',
      label: t('topicsCommon:category'),
    },
  ]

  useEffect(() => {
    reNewSettingsStatus && dispatchGetAboSettings()
  }, [reNewSettingsStatus],
  )

  useEffect(() => {
    let tempAboSettings: NetworkAboSettings = {}
    const menu: NetworkType[] = []
    aboSettings && aboSettings.map((settings: UserAboSettings) => {
      if (!settings.aboSettings[0]) {
        if (!tempAboSettings[settings.networkType as string]) {
          tempAboSettings = {
            ...tempAboSettings,
            [settings.networkType]: settings,
          }
        }
      } else {
        const networkPage = getNetworkPage(settings.networkType)
        if (networkPage && settings.aboSettings[0].pageIdentifier.networkPageId === networkPage.networkPageId) {
          tempAboSettings = {
            ...tempAboSettings,
            [settings.networkType]: settings,
          }
        }
      }

      if (settings.networkType === NetworkType.Facebook && !!settings.aboSettings && !!settings.aboSettings.length) {
        menu.push(NetworkType.Facebook)
      }
      if (settings.networkType === NetworkType.Instagram && !!settings.aboSettings && !!settings.aboSettings.length) {
        menu.push(NetworkType.Instagram)
      }
      if (settings.networkType === NetworkType.GoogleMyBusiness && !!settings.aboSettings && !!settings.aboSettings.length) {
        menu.push(NetworkType.GoogleMyBusiness)
      }
    })

    setTabs(menu)
    setNetworkAboSettings(tempAboSettings)
  }, [aboSettings, props.topicConfiguration?.networkPage],
  )

  useEffect(() => {
    props.topicConfiguration?.theme && dispatch(commonActions.setTheme(props.topicConfiguration?.theme))
  }, [props.topicConfiguration?.theme],
  )

  useEffect(() => {
    const status = actionOnTopicCreate?.status
    if (status && status === 'success') {
      backToOverview()
      dispatchGetAboTopics()
    }
  }, [actionOnTopicCreate])

  useEffect(() => {
    const status = actionOnTopicUpdate?.status
    if (status && status === 'success') {
      backToOverview()
      dispatchGetAboTopics()
    }
  }, [actionOnTopicUpdate])

  useEffect(() => {
    const status = actionOnTopicDelete?.status
    const error = actionOnTopicDelete?.error
    if (status && status === 'success') {
      dispatchGetAboTopics()
    }
    if (status && status === 'failed' && error && error.status === 500) {
      setToast({
        autoClose: false,
        open: true,
        type: 'error',
        title: t('topicsMessages:error'),
        message: t('topicsMessages:cannotDelete'),
        severity: 'error',
        requestActions: true,
        cancelLabel: null,
        confirmLabel: t('topicsCommon:ok'),
        handleClose: () => setToast(null),
        handleConfirm: () => {
          setToast(null)
        },
      })
    }
  }, [actionOnTopicDelete])

  useEffect(() => {
    if (editable) {
      dispatchGetAboTopics()
    } else {
      dispatchGetAboSettings()
    }
  }, [],
  )

  const handleNewTopic = () => {
    if (editMode === DisplayMode.popUp) setShowTopicPopUp(true)
  }

  const handleSelectedTopic = (topicId: string) => {
    if (editMode === DisplayMode.popUp) setShowTopicPopUp(true)
  }

  const backToOverview = () => {
    history.goBack()
  }

  const deleteTopicRequest = (topicId: string) => {
    deleteTopic(topicId)
  }

  useEffect(() => {
    const newArray: string[] = []
    Object.keys(aboTopicHasPostings).map(key => {
      if (aboTopicHasPostings[key].hasDeferredPosts) {
        setShowChangeDeferredRequest(true)
        newArray.push(key)
      }
    })
    setChangedSettingsTopicIds(newArray)
  }, [aboTopicHasPostings])

  const checkDeferredPostsForChangedSettings = (settings: UserAboSettingsUpdateElement[]) => {
    settings.map(elem => {
      const params: GetAboTopicHasPostingsRequest = {
        topicId: elem.topicId,
      }
      dispatchGetAboTopicHasPostings(params)
    })
  }

  /**
   * update Settings ist für die User (Schieberegler)
   * @param topicId
   * @param aboType
   */
  const editSettings = (topicId: string | undefined, aboType: AboType, networkType?: NetworkType) => {
    if (networkType) {
      const networkPage: any = getNetworkPage(networkType)
      if (topicId) {
        const settings = [{
          pageIdentifier: {
            networkType: networkType,
            networkPageId: networkPage.networkPageId,
          },
          topicId: topicId,
          aboType: aboType,
          notificationEnabled: true,
        }]
        const params: UpdateAboSettingsForCurrentUserRequest = {
          userAboSettingsUpdateRequest: {
            aboSettings: settings,
            updatePagePost: true,
          },
        }
        checkDeferredPostsForChangedSettings(settings)
        dispatchUpdateAboSettings(params)
      }
    }
  }

  /**
   * update Settings aller Kanäle ist für die User (Schieberegler)
   * @param newAboSettings
   */
  const editAboSettings = (newAboSettings: any[], networkType?: NetworkType) => {
    if (networkType) {
      const networkPage: any = getNetworkPage(networkType)
      const settings: UserAboSettingsUpdateElement[] = []

      newAboSettings.forEach((dataEntry) => {
        if (props.topicConfiguration) {
          settings.push({
            pageIdentifier: {
              networkType: networkType,
              networkPageId: networkPage.networkPageId,
            },
            topicId: dataEntry.topicId,
            aboType: dataEntry.aboType,
            notificationEnabled: true,
          })
        }
      })

      if (settings.length > 0) {
        const params: UpdateAboSettingsForCurrentUserRequest = {
          userAboSettingsUpdateRequest: {
            aboSettings: settings,
            updatePagePost: true,
          },
        }
        dispatchUpdateAboSettings(params)
        checkDeferredPostsForChangedSettings(settings)
      }
    }
  }

  const deleteTopic = (topicId: string) => {
    const params: DeleteAboTopicRequest = {
      topicId: topicId,
      forceCascadingDelete: true,
    }
    dispatchDeleteAboTopic(params)
  }

  const testSettingsChanges = (newData: Topic, oldData?: Topic) => {
    let settingsChanged = false
    if (newData.automaticPostingAllowed !== oldData?.automaticPostingAllowed) {
      settingsChanged = true
    }
    if (newData.manualPostingAllowed !== oldData?.manualPostingAllowed) {
      settingsChanged = true
    }
    if (newData.active !== oldData?.active) {
      settingsChanged = true
    }
    if (newData.noPostingAllowed !== oldData?.noPostingAllowed) {
      settingsChanged = true
    }
    return settingsChanged
  }

  /**
   * edit Topic ist für den Editor
   * @param data
   */
  const editTopic = (data: Topic) => {
    const settingsChanged = testSettingsChanges(data, aboTopics?.topics.find((elem) => elem.topic.id === data.id)?.topic)
    if (settingsChanged) {
      setToast({
        autoClose: false,
        open: true,
        type: 'error',
        title: t('topicsMessages:editSettingsRequestTitle'),
        message: t('topicsMessages:editSettingsRequest'),
        severity: 'error',
        requestActions: true,
        cancelLabel: t('topicsMessages:requestNo'),
        confirmLabel: t('topicsMessages:requestYes'),
        handleClose: () => setToast(null),
        handleConfirm: () => {
          setToast(null)
          if (data.id) {
            const params: UpdateAboTopicRequest = {
              topicId: data.id,
              topic: data,
              updateOption: TopicChangeCascadeLevel.AboSettings,
            }
            dispatchUpdateAboTopic(params)
          }
        },
        handleCancel: () => {
          setToast(null)
          if (data.id) {
            const params: UpdateAboTopicRequest = {
              topicId: data.id,
              topic: data,
              updateOption: TopicChangeCascadeLevel.None,
            }
            dispatchUpdateAboTopic(params)
          }
        },
      })
    } else {
      if (data.id) {
        const params: UpdateAboTopicRequest = {
          topicId: data.id,
          topic: data,
          updateOption: TopicChangeCascadeLevel.None,
        }
        dispatchUpdateAboTopic(params)
      }
    }
  }

  const createTopic = (data: Topic) => {
    const params: CreateAboTopicRequest = {
      topic: data,
    }
    dispatchCreateAboTopic(params)
  }

  const handleActiveTabChange = (activeTab: NetworkType) => {
    setActiveTab(activeTab)
  }

  const showTopicsOverview = (data: TopicWithMetadata[] | undefined, type?: string) => {
    return (
      <TopicsOverview
        tabs={tabs}
        type={type}
        editable={editable}
        editMode={editMode}
        integrateImages={integrateImages}
        data={data}
        licensePermissions={props.topicConfiguration?.licensePermissions}
        aboSettings={networkAboSettings}
        selectedTopic={handleSelectedTopic}
        editSettings={editSettings}
        editAboSettings={editAboSettings}
        newTopic={handleNewTopic}
        handleTabChange={handleActiveTabChange}
        setAcknowledgeSettings={(topicId: string, applyToDeferredPosts: boolean) => {
          const page = getNetworkPage(activeTab)
          page?.networkPageId && dispatchAcknowledgeSettingsForNetworkPage({
            topicId,
            networkType: activeTab,
            networkPageId: page.networkPageId,
            applyToDeferredPosts,
          })
        }}
        deleteTopic={(topicId: string) => deleteTopicRequest(topicId)}
      />)
  }

  return (
    <>
      {editable && <Submenu data={subMenu} />}
      {!editable && (
        <Switch>
          <Route exact path='/user/topics'>
            {showTopicsOverview(aboTopics?.topics)}
          </Route>
        </Switch>
      )}
      {editable && (
        <Switch>
          <Redirect exact from={`${path}`} to={`${path}/channels`} />
          <Route exact path={`${path}/channels`}>
            {showTopicsOverview(aboTopics?.topics.filter(item => item.topic.automaticPostingAllowed || item.topic.manualPostingAllowed), 'channel')}
          </Route>
          <Route exact path={`${path}/categories`}>
            {showTopicsOverview(aboTopics?.topics.filter(item => !(item.topic.automaticPostingAllowed || item.topic.manualPostingAllowed)), 'category')}
          </Route>
          <Route exact path={`${path}/:category/edit/:id`}>
            <TopicEdit
              aboTopics={aboTopics}
              initialValueDropdown={initialValueDropdown}
              editTopic={(data) => editTopic(data)}
              integrateImages={integrateImages}
              editMode={editMode}
              setShowTopicPopUp={setShowTopicPopUp}
              showTopicPopUp={showTopicPopUp}
            />
          </Route>
          <Route exact path={`${path}/:category/add_topic`}>
            <TopicEdit
              initialValueDropdown={initialValueDropdown}
              newTopic
              createTopic={(data) => createTopic(data)}
              integrateImages={integrateImages}
              editMode={editMode}
              setShowTopicPopUp={setShowTopicPopUp}
              showTopicPopUp={showTopicPopUp}
            />
          </Route>
        </Switch>
      )}
      {toast &&
        <DialogComponent
          type={toast.type || undefined}
          autoClose={toast.autoClose}
          title={toast.title}
          message={toast.message}
          open={toast.open}
          confirmLabel={toast.confirmLabel}
          cancelLabel={toast.cancelLabel}
          requestActions={toast.requestActions}
          onClose={toast.handleClose}
          onCancel={toast.handleCancel || undefined}
          onConfirm={toast.handleConfirm || undefined}
        />}
      <DialogComponent
        type='error'
        title={t('topicsMessages:editSettingsRequestTitle')}
        message={t('topicsMessages:changeDeferredSettingsRequest')}
        open={showChangeDeferredRequest}
        confirmLabel={t('topicsMessages:requestYes')}
        cancelLabel={t('topicsMessages:requestNo')}
        autoClose={false}
        requestActions
        onClose={() => setShowChangeDeferredRequest(false)}
        onCancel={() => {
          setShowChangeDeferredRequest(false)
          const page = getNetworkPage(activeTab)
          changedSettingsTopicIds.map(changedSettingsTopicId => {
            page?.networkPageId && dispatchAcknowledgeSettingsForNetworkPage({
              networkType: activeTab,
              networkPageId: page.networkPageId,
              topicId: changedSettingsTopicId,
              applyToDeferredPosts: false,
            })
          })
        }}
        onConfirm={() => {
          setShowChangeDeferredRequest(false)
          const page = getNetworkPage(activeTab)
          changedSettingsTopicIds.map(changedSettingsTopicId => {
            page?.networkPageId && dispatchAcknowledgeSettingsForNetworkPage({
              networkType: activeTab,
              networkPageId: page.networkPageId,
              topicId: changedSettingsTopicId,
              applyToDeferredPosts: true,
            })
          })
        }}
      />
    </>
  )
}
export default ProfessocialTopics
